import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { bff } from '@/lib/http/bff';
import AudioStemRow from './AudioStemRow';
import MixTransport from './MixTransport';
import UiSlider from './ui/UiSlider';
import { meterFromDb, gainToDb } from '../lib/audioMath';
import YoutubeSplitPanel from './YoutubeSplitPanel';
import useAudioMixEngine, { type StemLevels } from '../hooks/useAudioMixEngine';
import { prefetchBuffers } from '../hooks/mixEngine/bufferTransport';
import MninimalisticList from '@/shared/components/ui/MninimalisticList';
import MasterFrequencyResponse from './MasterFrequencyResponse';
import WaveformPlot from './WaveformPlot';
import MasterVuPro from './MasterVuPro';
// MasterEnvelope removed per request
import { clearAutoplayTimers } from '../hooks/stateGuards';
import { L } from '../utils/logger';
import { logDbSnapshot } from '../utils/dbProbe';
import { getMasterChainVersion } from '../hooks/mixEngine/graphNodes';

interface LibraryRow { id_scope: string|number; artist?: string; song?: string; album?: string }
interface LibraryRes { ok: boolean; rows: LibraryRow[] }
interface FilesRes { ok: boolean; id_scope: string|number; files: string[]; warning?: string }
interface Track { role: string; url: string; expiresAt?: number }
interface TracksRes { ok: boolean; id_scope: string|number; tracks: Track[]; warning?: string }
function fetchLibrary(): Promise<LibraryRes> { return bff.get(`/services/media/audio-library?_ts=${Date.now()}`); }
function fetchFiles(id_scope: string|number): Promise<FilesRes> { return bff.get(`/services/media/audio-library/${id_scope}/files`); }
function fetchTracks(id_scope: string|number): Promise<TracksRes> { return bff.get(`/services/media/audio-library/${id_scope}/tracks`); }

const AudioLibraryPlayer: React.FC = () => {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<LibraryRow | null>(null);

  const libQuery = useQuery({ queryKey:['audioLibrary'], queryFn: fetchLibrary, refetchInterval: 30000 });
  const filesQuery = useQuery({ queryKey:['audioLibraryFiles', selected?.id_scope], queryFn: () => fetchFiles(selected!.id_scope), enabled: !!selected });
  const tracksQuery = useQuery({ queryKey:['audioLibraryTracks', selected?.id_scope], queryFn: () => fetchTracks(selected!.id_scope), enabled: !!selected });

  // Mix engine wiring for current tracks
  const useBufferMode = true;
  const mix = useAudioMixEngine({ tracks: (tracksQuery.data?.tracks || []).map(t => ({ role: t.role, url: t.url })), bufferMode: useBufferMode });
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);

  const [uiSpeed, setUiSpeed] = useState(1.0);
  const [uiPitchSt, setUiPitchSt] = useState(0); // semitones, -12..+12
  const onSpeedChange = (newRate: number) => {
    if (mix.maintainPitch) {
      // update UI state
      setUiSpeed(newRate);
  // update master playback rate; orchestrator will map to WA vs RB
  mix.setMasterPlaybackRate(newRate);
    } else {
      // normal mode, follow WebAudio rate
      setUiSpeed(newRate); // keep UI in sync
      mix.setMasterPlaybackRate(newRate);
    }
  };
  // Auto-select first row when loaded
  useEffect(()=>{ if(!selected && libQuery.data?.rows?.length) setSelected(libQuery.data.rows[0]); },[libQuery.data, selected]);

  // On component unmount, clear any autoplay timers that might still be around
  useEffect(() => {
    return () => { try { clearAutoplayTimers(); } catch {} };
  }, []);

  // When scope changes, notify engine
  useEffect(() => {
    if (!selected?.id_scope) return;
    L.info('media:scope', 'select', { scope: String(selected.id_scope) });
    mix.onScopeChange();
  }, [selected?.id_scope]);

  // Initialize UI pitch from engine on mount/scope change
  useEffect(() => {
    const st = Number((mix as any).dsp?.pitch?.getSemitones?.() ?? 0);
    if (Number.isFinite(st)) setUiPitchSt(Math.max(-12, Math.min(12, st)));
  L.info('media:transport', 'uiInitFromEngine.pitch', { st });
  }, [selected?.id_scope]);

  // Auto-play when tracks are ready for the selected scope
  const autoPlayedScopeRef = useRef<string | number | null>(null);
  useEffect(() => {
    if (!selected?.id_scope) return;
    const tracks = tracksQuery.data?.tracks || [];
    if (tracks.length === 0) return;
    if (autoPlayedScopeRef.current === selected.id_scope) return;
    autoPlayedScopeRef.current = selected.id_scope;
    // Defer to next tick to ensure <audio> refs are attached and graph is wired
    // Clear any previous autoplay timers before scheduling a new attempt
    try { clearAutoplayTimers(); } catch {}
  const to = setTimeout(() => {
      L.info('media:transport', 'autoplay.try', { scope: String(selected.id_scope), tracks: tracks.length });
      // Also clear before starting a new attempt, in case something raced
      try { clearAutoplayTimers(); } catch {}
      void mix.play().catch(() => { setAutoplayBlocked(true); });
      // After a short delay, check if AudioContext is running (via analyser context)
      const check = setTimeout(() => {
        const an = mix.getMasterAnalyser?.();
        const running = !!an && (an.context?.state === 'running');
        if (!running) {
          L.warn('media:health', 'autoplay.blocked', { ctxState: an?.context?.state });
          setAutoplayBlocked(true);
        }
      }, 300);
      // Clear the blocked banner automatically when audio starts
      const clear = setInterval(() => {
        const an = mix.getMasterAnalyser?.();
        if (an && an.context?.state === 'running') {
          L.info('media:transport', 'autoplay.ok');
          setAutoplayBlocked(false);
          clearInterval(clear);
        }
      }, 500);
      // store clears on window
      (window as any).__mediaAutoplayTimers ??= [];
      (window as any).__mediaAutoplayTimers.push(check, clear);
    }, 0);
    return () => {
      clearTimeout(to);
      // Cleanup on scope-change or unmount: clear all autoplay timers
      try { clearAutoplayTimers(); } catch {}
    };
  }, [selected?.id_scope, tracksQuery.data?.tracks?.length]);

  // Prefetch decoded buffers for current selection to reduce latency on scope start
  useEffect(() => {
    const tracks = tracksQuery.data?.tracks || [];
    if (!tracks.length) return;
  const actx = (mix as any)?.getMasterAnalyser?.()?.context as AudioContext | undefined;
  // Enforce single AudioContext
  const ctx = actx || require('../hooks/mixEngine/orchestrator/getSharedAudioContext').getSharedAudioContext();
    if (!ctx) return;
    void prefetchBuffers(ctx, tracks.map(t => ({ url: t.url })));
  }, [tracksQuery.data?.tracks]);

  // Re-apply last pitch to all stems whenever new tracks for the current scope are wired
  useEffect(() => {
    const tracks = tracksQuery.data?.tracks || [];
    if (!tracks.length) return;
    const scale = Math.pow(2, uiPitchSt / 12);
    try { (mix as any).dsp?.setRbPitch?.('*', scale); } catch {}
  }, [tracksQuery.data?.tracks?.length, uiPitchSt]);


  const play = mix.play;
  const handlePlay = async () => {
    // Always resume AudioContext on explicit user gesture before transport ops
    try {
      const { getCtx } = await import('../hooks/mixEngine/ctxSingleton');
      const ctx = getCtx();
      if (ctx.state === 'suspended') { try { await ctx.resume(); } catch {} }
    } catch {}
    try { await (mix as any).preflightResetIfNeeded?.(); } catch {}
    await play();
  };
  const pause = mix.pause;
  const stop = mix.stop;
  const seekAll = (time: number) => { void mix.seek(time, true); };

  const rowClick = (row: LibraryRow) => {
    setSelected(row);
    qc.invalidateQueries({ queryKey:['audioLibraryFiles', row.id_scope] });
    qc.invalidateQueries({ queryKey:['audioLibraryTracks', row.id_scope] });
  };

  return (
    <div className="flex flex-col md:flex-row gap-6 items-stretch h-full">
      {/* Library Frame (left column: 66% on md+) */}
      <div className="w-full md:w-2/3 rounded-xl border border-neutral-700/40 bg-neutral-900/30 p-4 flex flex-col h-full">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-neutral-300">Library</h2>
        </div>
        <div className="flex-1 overflow-auto pr-1">
          {libQuery.isLoading && <div className="text-xs text-neutral-500">Loading…</div>}
          {libQuery.isError && <div className="text-xs text-rose-500">Load failed</div>}
          {libQuery.data && libQuery.data.rows.length === 0 && (
            <div className="text-neutral-500 text-xs">No audio metadata yet</div>
          )}
          {libQuery.data && libQuery.data.rows.length > 0 && (
            <MninimalisticList
              items={libQuery.data.rows}
              getKey={(r) => String(r.id_scope)}
              renderText={(r) => {
                const artist = (r.artist || '').trim();
                const song = (r.song || '').trim();
                const album = (r.album || '').trim();
                let text = '';
                if (artist && song) text = `${artist} – ${song}`;
                else if (song) text = song;
                else if (artist) text = artist;
                else text = String(r.id_scope);
                if (album) text += ` (${album})`;
                return text;
              }}
              isSelected={(r) => !!(selected && r.id_scope === selected.id_scope)}
              onItemClick={(r) => rowClick(r)}
              size="sm"
            />
          )}
        </div>
        {/* Youtube Split anchored at bottom of the same frame */}
        <div className="mt-3 pt-3 border-t border-neutral-700/40">
          <YoutubeSplitPanel />
        </div>
      </div>

  {/* Player Frame (right column: 33% on md+) */}
  <div className="w-full md:w-1/3 rounded-xl border border-neutral-700/40 p-6 bg-neutral-900/30 overflow-auto flex flex-col h-full">
        <h2 className="text-lg font-semibold text-neutral-200">Player</h2>
        {process.env.NODE_ENV === 'development' && (
          <pre className="text-[10px] text-neutral-400 max-h-24 overflow-auto my-2">
            {JSON.stringify((window as any).__LOGS__?.slice(-10), null, 2)}
          </pre>
        )}
        {selected && (
          <div className="text-xs text-neutral-400 mb-2">Scope: {String(selected.id_scope)} · {selected.artist} – {selected.song} {selected.album && `(${selected.album})`}</div>
        )}

          {tracksQuery.isLoading && <div className="text-xs text-neutral-500">Preparing tracks…</div>}
          {tracksQuery.data && tracksQuery.data.tracks.length === 0 && (
            <div className="text-xs text-neutral-500">
              No tracks for this scope
              {tracksQuery.data.warning === 'upstream_422' && ' (upstream 422)'}
              {tracksQuery.data.warning === 'upstream_422_fallback' && ' (retried broad fetch)'}
              {tracksQuery.data.warning === 'upstream_422_fallback_failed' && ' (fallback failed)'}
              .
            </div>
          )}

          {tracksQuery.data && tracksQuery.data.tracks.length > 0 && (
            <div className="mb-4 space-y-2">
              {/* Master label */}
              <div className="text-xs text-neutral-300 mb-1">Master</div>

              {/* VU full-width (no limiter/GR visuals) */}
              <div className="mb-2">
                {(() => {
                  try {
                    const post = (mix as any)?.getMasterAnalyser?.() as AnalyserNode | null;
                    return (
                      <MasterVuPro
                        postAnalyser={post}
                        height={12}
                        floorDb={-60}
                        ceilingDb={-0.1}
                        showScale={false}
                        showDbfs={true}
                        className="rounded w-full"
                      />
                    );
                  } catch { return null; }
                })()}
              </div>

              {/* Level slider full-width, then a tiny row with value and % */}
              <div className="mb-3">
                <UiSlider
                  value={mix.masterVolume}
                  min={0}
                  max={Math.pow(10, 6 / 20)}
                  step={0.01}
                  onChange={mix.setMasterVolume}
                  aria-label="Master volume"
                />
                <div className="flex justify-between mt-1">
                  {/* attenuation (master × gate) in dB */}
                  <span
                    className="text-[10px] text-neutral-400 cursor-pointer hover:text-neutral-200"
                    title="Reset master level to 0 dB (100%)"
                    onClick={() => mix.setMasterVolume(1)}
                  >
                    {(() => {
                      try {
                        const refs: any = (mix as any)?._refs ?? (mix as any)?.refs ?? {};
                        const gate = Number(refs?.gateGainRef?.current?.gain?.value ?? 1);
                        const mg = Number(refs?.masterGainRef?.current?.gain?.value ?? mix.masterVolume ?? 1);
                        const g = Math.max(0, gate * mg);
                        const attDb = gainToDb(g);
                        return isFinite(attDb) ? `${attDb.toFixed(1)} dB` : '-∞ dB';
                      } catch { return '\u00A0'; }
                    })()}
                  </span>
                  <span
                    className="text-[10px] text-neutral-400 cursor-pointer hover:text-neutral-200"
                    title="Reset master level to 100%"
                    onClick={() => mix.setMasterVolume(1)}
                  >
                    {Math.round(mix.masterVolume*100)}%
                  </span>
                </div>
              </div>

              {tracksQuery.data.tracks.map(t => (
                <AudioStemRow
                  key={t.url}
                  role={t.role}
                  volume={mix.volumes[t.role] ?? 1}
                  muted={!!mix.muted[t.role]}
                  solo={!!mix.solo[t.role]}
                  levelPre={mix.levels[t.role]?.preMeter ?? 0}
                  level={(() => {
                    const srcDb = mix.levels[t.role]?.srcDb;
                    if (typeof srcDb !== 'number') return 0;
                    const g = mix.getEffectiveGain(t.role);
                    if (g === 0) return 0;
                    const att = gainToDb(g);
                    const outDb = srcDb + att;
                    return meterFromDb(outDb);
                  })()}
                  levelDbfs={(() => {
                    const srcDb = mix.levels[t.role]?.srcDb;
                    if (typeof srcDb !== 'number') return undefined;
                    const g = mix.getEffectiveGain(t.role);
                    if (g === 0) return srcDb;
                    const att = gainToDb(g);
                    return isFinite(att) ? srcDb + att : undefined;
                  })()}
                  attenuationDb={mix.levels[t.role]?.attenuationDb}
                  onVolumeChange={(v) => mix.setVolume(t.role, v)}
                  onToggleMute={() => mix.toggleMute(t.role)}
                  onToggleSolo={() => mix.toggleSolo(t.role)}
                />
              ))}
            </div>
          )}

          <div className="flex items-center gap-3 mb-4">
            <MixTransport
              duration={mix.duration}
              currentTime={mix.currentTime}
              status={mix.status}
              mediaRate={mix.masterPlaybackRate}
              onPlay={handlePlay}
              onPause={pause}
              onStop={stop}
              onSeek={seekAll}
            />
            {autoplayBlocked && (
              <button
                className="ml-3 px-2 py-1 text-xs rounded bg-emerald-600 text-white hover:bg-emerald-500"
                onClick={() => { setAutoplayBlocked(false); void mix.play().catch(()=>{}); }}
              >
                Enable audio
              </button>
            )}
          </div>

          {/* Time-stretch (master playback rate) slider */}
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs w-24 shrink-0 text-neutral-300">Speed</span>
            <div className="flex-1">
              <UiSlider
                value={mix.maintainPitch ? uiSpeed : mix.masterPlaybackRate}
                min={0.5}
                max={2.0}
                step={0.01}
                onChange={(newRate) => {
                  const safe = Math.max(0.5, Math.min(2.0, newRate));
                  if (mix.maintainPitch) {
                    // UI-only state
                    setUiSpeed(safe);
                    // Update master rate; orchestrator handles mapping
                    mix.setMasterPlaybackRate(safe);
                  } else {
                    setUiSpeed(safe);
                    mix.setMasterPlaybackRate(safe);
                  }
                }}
                aria-label="Playback speed"
              />
            </div>
            <span
              className="text-[10px] w-12 text-right text-neutral-400 cursor-pointer hover:text-neutral-200"
              title="Reset speed to 100%"
              onClick={() => {
                // Preserve current UI pitch when resetting speed
                try { if (mix.maintainPitch) (mix as any).dsp?.pitch?.setSemitones?.(uiPitchSt); } catch {}
                // Reset to 1.0x regardless of mode
                setUiSpeed(1.0);
                mix.setMasterPlaybackRate(1.0);
                // Re-affirm pitch on the next tick to win any racing mapping updates
                if (mix.maintainPitch) {
                  setTimeout(() => { try { (mix as any).dsp?.pitch?.setSemitones?.(uiPitchSt); } catch {} }, 0);
                }
              }}
            >
              {Math.round((mix.maintainPitch ? uiSpeed : mix.masterPlaybackRate) * 100)}%
            </span>
            {/* Vinyl Mode (inverse of Maintain pitch). Default OFF. */}
            <label className="flex items-center gap-1 ml-3 text-[10px] text-neutral-300 select-none" title="When ON, speed affects pitch like a turntable">
              <input
                type="checkbox"
                className="accent-emerald-500"
                checked={!mix.maintainPitch}
                onChange={(e) => {
                  const vinyl = e.target.checked;
                  // Capture the current effective speed BEFORE toggling
                  const rateToApply = mix.maintainPitch ? uiSpeed : mix.masterPlaybackRate;
                  // Inverse mapping: vinyl ON => maintainPitch OFF
                  mix.setMaintainPitch(!vinyl);
                  // Always reset pitch to default when toggling Vinyl Mode to avoid mismatches
                  setUiPitchSt(0);
                  try { (mix as any).dsp?.pitch?.setSemitones?.(0); } catch {}
                  // Also force-sync speed so Vinyl Mode immediately reflects current slider value
                  // Apply the captured rate to both UI and engine
                  try {
                    const safe = Math.max(0.5, Math.min(2.0, rateToApply || 1.0));
                    setUiSpeed(safe);
                    mix.setMasterPlaybackRate(safe);
                  } catch {}
                }}
              />
              Vinyl Mode
            </label>
          </div>

          {/* Pitch shift slider (-12..+12 semitones). Disabled in Vinyl Mode. */}
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs w-24 shrink-0 text-neutral-300">Pitch</span>
            <div className="flex-1">
              <UiSlider
                value={uiPitchSt}
                min={-12}
                max={12}
                step={0.1}
                disabled={!mix.maintainPitch}
                onChange={(st) => {
                  const clamped = Math.max(-12, Math.min(12, st));
                  setUiPitchSt(clamped);
                  try { (mix as any).dsp?.pitch?.setSemitones?.(clamped); } catch {}
                }}
                aria-label="Pitch shift (semitones)"
              />
            </div>
            <span
              className={`text-[10px] w-12 text-right ${mix.maintainPitch ? 'text-neutral-400 cursor-pointer hover:text-neutral-200' : 'text-neutral-600 cursor-not-allowed'}`}
              title={mix.maintainPitch ? 'Reset pitch to 0 st' : 'Disabled in Vinyl Mode'}
              onClick={() => {
                if (!mix.maintainPitch) return;
                setUiPitchSt(0);
                try { (mix as any).dsp?.pitch?.setSemitones?.(0); } catch {}
              }}
            >
              {uiPitchSt.toFixed(1)} st
            </span>
          </div>

          {/* Master waveform (time domain) - height aligned to frequency response (160) */}
          <div className="mb-4">
            <div className="text-xs text-neutral-400 mb-1">Master Waveform</div>
            <div className="rounded-md border border-neutral-700/40 bg-black/60 overflow-hidden w-full">
              <WaveformPlot
                analyser={mix.getMasterAnalyser()}
                status={mix.status}
                width={700}
                height={160}
                fps={mix.dsp.waveform.fps}
                color="#9fffd8"
                background="#131313"
                showGrid={mix.dsp.waveform.grid}
                gridHorizLines={mix.dsp.waveform.horiz}
                gridVertLines={mix.dsp.waveform.vert}
              />
            </div>
          </div>

          {/* Master frequency response */}
          <div className="mb-4">
            <div className="text-xs text-neutral-400 mb-1">Master Frequency Response</div>
            <div className="rounded-md border border-neutral-700/40 bg-black/60 overflow-hidden w-full">
              <MasterFrequencyResponse
                analyser={mix.getMasterAnalyser()}
                filter={mix.getMasterFilter()}
                highpass={mix.getMasterHighpassFilter()}
                eqEnabled={mix.dsp.eq}
                eqHpfEnabled={mix.dsp.eqHpf}
                eqLpfEnabled={mix.dsp.eqLpf}
                status={mix.status}
                width={600}
                height={160}
                cutoffHz={mix.masterLowpassHz}
                onCutoffChange={mix.setMasterLowpassHz}
                hpfHz={mix.masterHighpassHz}
                onHpfChange={mix.setMasterHighpassHz}
              />
            </div>
          </div>

          {/* dBFS meters removed */}

          {/* Master envelope removed */}

          {/* DSP controls and engine debug removed per request */}

          {/* Hidden <audio> elements removed: engine now fully buffer/worklet-driven */}
      </div>
    </div>
  );
};

export default AudioLibraryPlayer;

